/**
 * Organization Provider
 *
 * Provides organization context to the application.
 * Fetches the current user's organization and pending invites.
 */

import { createContext, useContext, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient, ApiError } from "@/services/api";
import { useApiReady } from "@/hooks/useApi";
import type { Organization, OrganizationInvite } from "@/types/organization";
import type { UserData } from "@/types/user";

interface OrganizationContextValue {
  user: UserData | null;
  organization: Organization | null;
  pendingInvites: OrganizationInvite[];
  isLoading: boolean;
  hasOrganization: boolean;
  isOrganizationOwner: boolean;
  isAuthError: boolean;
  refetchUser: () => void;
  refetchOrganization: () => void;
  refetchInvites: () => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextValue | null>(null);

interface OrganizationProviderProps {
  children: ReactNode;
}

export function OrganizationProvider({ children }: OrganizationProviderProps) {
  const isApiReady = useApiReady();

  // Fetch current user
  const {
    data: user,
    isLoading: userLoading,
    error: userError,
    refetch: refetchUser,
  } = useQuery<UserData>({
    queryKey: ["user", "me"],
    queryFn: () => apiClient.get<UserData>("/api/v1/user/me/"),
    staleTime: 0, // Always fetch fresh - organization_id can change
    gcTime: 30000, // Keep in cache for 30s for background updates
    enabled: isApiReady,
  });

  // Fetch organization if user has one
  const {
    data: organization,
    isLoading: orgLoading,
    refetch: refetchOrganization,
  } = useQuery<Organization>({
    queryKey: ["organization", "me"],
    queryFn: () => apiClient.get<Organization>("/api/v1/organization/me"),
    staleTime: 0, // Always fetch fresh
    gcTime: 30000,
    enabled: isApiReady && !!user?.organization_id,
    retry: false,
  });

  // Fetch pending invites
  const {
    data: pendingInvites,
    isLoading: invitesLoading,
    refetch: refetchInvites,
  } = useQuery<OrganizationInvite[]>({
    queryKey: ["invites", "pending"],
    queryFn: () => apiClient.get<OrganizationInvite[]>("/api/v1/invites/pending"),
    staleTime: 0, // Always fetch fresh data for invites
    gcTime: 0, // Don't cache invite data
    enabled: isApiReady,
  });

  const isLoading = userLoading || (!!user?.organization_id && orgLoading) || invitesLoading;
  const hasOrganization = !!user?.organization_id && !!organization;
  const isOrganizationOwner = hasOrganization && organization?.owner_user_id === user?.id;
  const isAuthError = userError instanceof ApiError && userError.status === 401;

  const value: OrganizationContextValue = {
    user: user ?? null,
    organization: organization ?? null,
    pendingInvites: pendingInvites ?? [],
    isLoading,
    hasOrganization,
    isOrganizationOwner,
    isAuthError,
    refetchUser: () => refetchUser(),
    refetchOrganization: () => refetchOrganization(),
    refetchInvites: async () => {
      await refetchInvites();
    },
  };

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
}

export function useOrganizationContext() {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error("useOrganizationContext must be used within an OrganizationProvider");
  }
  return context;
}
